import { SceneSignature } from './monitoringFrames';

export interface MovementThresholds {
  baselineWarmupDurationMs: number;
  baselineSampleCount: number;
  warmupStructuralNoiseThreshold: number;
  activationScoreThreshold: number;
  structuralChangeThreshold: number;
  consecutiveSampleRequirement: number;
  movementConfirmationMs: number;
  recoveryScoreThreshold: number;
  recoveryStructuralThreshold: number;
  recoveryDurationMs: number;
  rebaselineDelayMs: number;
}

export interface SceneComparison {
  score: number;
  structural: number;
  histogram: number;
  luminance: number;
  edge: number;
}

export interface MovementResult extends SceneComparison {
  moved: boolean;
  baselineReady: boolean;
  baselineRebuilt: boolean;
  phase: 'warming' | 'stable' | 'confirming' | 'moved' | 'recovering';
}

const emptyComparison = { score: 0, structural: 0, histogram: 0, luminance: 0, edge: 0 };

export function compareSceneSignatures(baseline: SceneSignature, current: SceneSignature): SceneComparison {
  const baselineMean = baseline.blocks.reduce((total, value) => total + value, 0) / baseline.blocks.length;
  const currentMean = current.blocks.reduce((total, value) => total + value, 0) / current.blocks.length;
  let structural = 0;
  for (let index = 0; index < baseline.blocks.length; index += 1) {
    structural += Math.abs((baseline.blocks[index] - baselineMean) - (current.blocks[index] - currentMean));
  }
  structural = (structural / baseline.blocks.length / 255) * 100;
  let histogram = 0;
  for (let index = 0; index < baseline.histogram.length; index += 1) histogram += Math.abs(baseline.histogram[index] - current.histogram[index]);
  histogram = (histogram / 2) * 100;
  const luminance = (Math.abs(baseline.luminance - current.luminance) / 255) * 100;
  const edge = (Math.abs(baseline.edgeStrength - current.edgeStrength) / 255) * 100;
  return {
    structural,
    histogram,
    luminance,
    edge,
    score: structural * 0.68 + histogram * 0.12 + edge * 0.15 + luminance * 0.05
  };
}

function averageSignatures(samples: SceneSignature[]): SceneSignature {
  const blocks = new Float32Array(samples[0].blocks.length);
  const histogram = new Float32Array(samples[0].histogram.length);
  let luminance = 0;
  let edgeStrength = 0;
  samples.forEach((sample) => {
    sample.blocks.forEach((value, index) => { blocks[index] += value / samples.length; });
    sample.histogram.forEach((value, index) => { histogram[index] += value / samples.length; });
    luminance += sample.luminance / samples.length;
    edgeStrength += sample.edgeStrength / samples.length;
  });
  return { blocks, histogram, luminance, edgeStrength };
}

export class MobileMovementDetector {
  private baseline?: SceneSignature;
  private warmupSamples: SceneSignature[] = [];
  private warmupStartedAt?: number;
  private activationStartedAt?: number;
  private consecutiveMovementSamples = 0;
  private recoveryStartedAt?: number;
  private moved = false;
  private baselineBuiltBefore = false;
  private warmupNotBefore = 0;

  constructor(private readonly thresholds: MovementThresholds) {}

  reset(now = 0): void {
    this.baseline = undefined;
    this.warmupSamples = [];
    this.warmupStartedAt = undefined;
    this.activationStartedAt = undefined;
    this.consecutiveMovementSamples = 0;
    this.recoveryStartedAt = undefined;
    this.moved = false;
    this.warmupNotBefore = now;
  }

  update(signature: SceneSignature, now: number): MovementResult {
    if (!this.baseline) return this.warmBaseline(signature, now);
    const comparison = compareSceneSignatures(this.baseline, signature);

    if (!this.moved) {
      const changed = comparison.score >= this.thresholds.activationScoreThreshold && comparison.structural >= this.thresholds.structuralChangeThreshold;
      if (!changed) {
        this.activationStartedAt = undefined;
        this.consecutiveMovementSamples = 0;
        return { ...comparison, moved: false, baselineReady: true, baselineRebuilt: false, phase: 'stable' };
      }
      this.activationStartedAt ??= now;
      this.consecutiveMovementSamples += 1;
      const confirmed = this.consecutiveMovementSamples >= this.thresholds.consecutiveSampleRequirement && now - this.activationStartedAt >= this.thresholds.movementConfirmationMs;
      if (confirmed) this.moved = true;
      return { ...comparison, moved: this.moved, baselineReady: true, baselineRebuilt: false, phase: this.moved ? 'moved' : 'confirming' };
    }

    const recovered = comparison.score <= this.thresholds.recoveryScoreThreshold && comparison.structural <= this.thresholds.recoveryStructuralThreshold;
    if (!recovered) {
      this.recoveryStartedAt = undefined;
      return { ...comparison, moved: true, baselineReady: true, baselineRebuilt: false, phase: 'moved' };
    }
    this.recoveryStartedAt ??= now;
    if (now - this.recoveryStartedAt < this.thresholds.recoveryDurationMs) {
      return { ...comparison, moved: true, baselineReady: true, baselineRebuilt: false, phase: 'recovering' };
    }
    this.baselineBuiltBefore = true;
    this.reset(now + this.thresholds.rebaselineDelayMs);
    return { ...comparison, moved: false, baselineReady: false, baselineRebuilt: false, phase: 'warming' };
  }

  private warmBaseline(signature: SceneSignature, now: number): MovementResult {
    if (now < this.warmupNotBefore) return { ...emptyComparison, moved: false, baselineReady: false, baselineRebuilt: false, phase: 'warming' };
    const previous = this.warmupSamples.at(-1);
    if (previous && compareSceneSignatures(previous, signature).structural > this.thresholds.warmupStructuralNoiseThreshold) {
      this.warmupSamples = [];
      this.warmupStartedAt = undefined;
    }
    this.warmupStartedAt ??= now;
    this.warmupSamples.push(signature);
    if (this.warmupSamples.length > this.thresholds.baselineSampleCount) this.warmupSamples.shift();
    const ready = this.warmupSamples.length >= this.thresholds.baselineSampleCount && now - this.warmupStartedAt >= this.thresholds.baselineWarmupDurationMs;
    if (!ready) return { ...emptyComparison, moved: false, baselineReady: false, baselineRebuilt: false, phase: 'warming' };
    this.baseline = averageSignatures(this.warmupSamples);
    const rebuilt = this.baselineBuiltBefore;
    this.warmupSamples = [];
    this.warmupStartedAt = undefined;
    return { ...emptyComparison, moved: false, baselineReady: true, baselineRebuilt: rebuilt, phase: 'stable' };
  }
}
